import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { CollectionScoringStatus, CollectionScoringSummary } from '../../generated';
import { listBenchmarkSelfScoringsV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/scorings: the product categories a benchmark has been
 * scored under, most recently selected first. `toBenchmarkScoring` is the one place the response is mapped. Tests
 * answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

export type BenchmarkScoringStatus = CollectionScoringStatus;

/**
 * One scoring of a benchmark under a product category. The listing answers the generated `CollectionScoringSummary`;
 * the request route answers `CollectionScoringView`, which has no category name, so the name is optional here. A
 * benchmark's own scoring never has a `retailer`, so it is left out.
 */
export type BenchmarkScoringResponse = Omit<CollectionScoringSummary, 'retailer' | 'product_category_name'> & {
  product_category_name?: string | null;
};

/** A scoring of a benchmark under a product category, mapped for the UI. */
export interface BenchmarkScoring {
  id: string;
  productCategoryId: string;
  /** `null` when the route did not include it; callers can resolve it from the product categories. */
  productCategoryName: string | null;
  /** Pending while the benchmark's images are being scored; a scoring pending for 30 minutes turns failed. */
  status: BenchmarkScoringStatus;
  requestedAt: string;
  readyAt: string | null;
  /** When the user last picked this category; the most recent one is the page's initial selection. */
  lastSelectedAt: string | null;
}

export function toBenchmarkScoring(response: BenchmarkScoringResponse): BenchmarkScoring {
  return {
    id: response.id,
    productCategoryId: response.product_category_id,
    productCategoryName: response.product_category_name ?? null,
    status: response.status,
    requestedAt: response.requested_at,
    readyAt: response.ready_at,
    lastSelectedAt: response.last_selected_at,
  };
}

export interface ListBenchmarkScoringsQueryVariables {
  workspace_id: string;
  benchmark_id: string;
}

export type ListBenchmarkScoringsQuery = BenchmarkScoring[];

async function listBenchmarkScorings({ workspace_id, benchmark_id }: ListBenchmarkScoringsQueryVariables) {
  const { data } = await listBenchmarkSelfScoringsV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdScoringsGet({
    path: { workspace_id, benchmark_id },
    throwOnError: true,
  });
  return (data ?? []).map(toBenchmarkScoring);
}

export const useListBenchmarkScoringsQuery = <TData = ListBenchmarkScoringsQuery, TError = unknown>(
  variables: ListBenchmarkScoringsQueryVariables,
  options?: Omit<UseQueryOptions<ListBenchmarkScoringsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ListBenchmarkScoringsQuery, TError, TData>({
    queryKey: useListBenchmarkScoringsQuery.getKey(variables),
    queryFn: () => listBenchmarkScorings(variables),
    ...options,
  });
};

useListBenchmarkScoringsQuery.getKey = ({ workspace_id, benchmark_id }: ListBenchmarkScoringsQueryVariables) => [
  'ListBenchmarkScorings',
  { workspace_id, benchmark_id },
];

useListBenchmarkScoringsQuery.fetcher = (variables: ListBenchmarkScoringsQueryVariables) => () =>
  listBenchmarkScorings(variables);
